import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, DoorClosed, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { Avatar, Cargando, EnVivo, botonClaro, display, panel } from '../asistencia/ui';
import { Ascensor, Santamaria } from './Ilustraciones';
import { Encabezado, MantenerParaConfirmar } from './ui';
import {
    ACCIONES, CLASE_TONO, TONO, armarElementos, cuando, esSantamaria, estadoPuerta, estadoSantamaria, momento,
    queEvento, quienEvento, tonoEvento,
} from './comun';

const MOMENTO_MS = 6000;   // lo que el dibujo del ascensor queda abierto tras un pase

const ABIERTOS = ['abierta', 'siempre_abierta', 'quedo_abierta', 'forzada'];

// "las 7:31 a. m." hoy; "ayer a las 7:31 a. m."; "el 21/10 a las 7:31 a. m."
const desde = (iso) => {
    const c = cuando(iso);
    return c.startsWith('a las') ? c.slice(2) : c;
};

// "hoy a las 7:31 a. m."; "ayer a las ..."; "el 21/10 a las ..."
const cuandoFue = (iso) => {
    const c = cuando(iso);
    return c.startsWith('a las') ? `hoy ${c}` : c;
};

const minuscula = (t) => (t ? t.charAt(0).toLowerCase() + t.slice(1) : t);

// Cierra una frase sin repetir el punto de "a. m." o "p. m."
const frase = (t) => (t.endsWith('.') ? t : `${t}.`);

// Cómo están, en una o dos frases
const resumen = (elementos) => {
    const todas = elementos.flatMap((e) => e.puertas);
    if (todas.length && todas.every((p) => !p.en_linea)) {
        return 'GeoVision no ve al controlador ALPHA: las puertas pueden seguir abriendo con huella, pero aquí no se ven.';
    }
    const frases = [];
    const santa = elementos.find((e) => e.tipo === 'santamaria');
    if (santa) {
        const s = estadoSantamaria(santa);
        if (s.clave !== 'desconocido') frases.push(frase(`La santamaría está ${s.clave} desde ${desde(s.ultimo.fecha_hora)}`));
    }
    const ascensores = elementos.filter((e) => e.tipo === 'ascensor');
    const distintos = ascensores.filter((a) => estadoPuerta(a.puerta).clave !== 'cerrada');
    if (distintos.length) {
        distintos.forEach((a) => frases.push(frase(`${a.nombre}: ${minuscula(estadoPuerta(a.puerta).texto)}`)));
    } else if (ascensores.length) {
        frases.push(ascensores.length === 1 ? 'El ascensor está en normal.' : 'Los ascensores están en normal.');
    }
    return frases.join(' ');
};

const Escenario = ({ children }) => (
    <div className="puerta-escenario rounded-xl border border-slate-100 h-44 sm:h-48 px-3 py-2 overflow-hidden">{children}</div>
);

// Lo último que pasó ahí, con quién fue
const LoUltimo = ({ eventos, recientes, onVerTodo }) => (
    <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="flex items-baseline justify-between gap-3 mb-1.5">
            <h3 className="text-xs font-medium text-slate-500">Lo último</h3>
            <button type="button" onClick={onVerTodo} className="text-xs text-sura-blue hover:underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60">
                Ver todos los registros
            </button>
        </div>
        {eventos.length === 0 ? <p className="text-sm text-slate-400 py-2">Sin registros todavía.</p> : (
            <ul>
                {eventos.slice(0, 4).map((ev) => {
                    const quien = quienEvento(ev);
                    return (
                        <li key={ev.id} className={`flex items-center gap-3 py-1.5 rounded-lg ${recientes.has(ev.id) ? 'puerta-recien' : ''}`}>
                            {quien ? <Avatar persona={{ nombre: quien, foto: ev.empleado?.foto }} size={28} /> : (
                                <span className="w-7 h-7 rounded-full shrink-0 inline-flex items-center justify-center border border-dashed border-slate-300 text-slate-400" aria-hidden>
                                    <DoorClosed size={13} />
                                </span>
                            )}
                            <span className="flex-1 min-w-0 leading-tight">
                                <span className="block text-sm text-slate-800 truncate">{quien || queEvento(ev)}</span>
                                {quien && <span className={`block text-xs truncate ${CLASE_TONO[tonoEvento(ev)]}`}>{queEvento(ev)}</span>}
                            </span>
                            <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">{momento(ev.fecha_hora)}</span>
                        </li>
                    );
                })}
            </ul>
        )}
    </div>
);

const TarjetaSantamaria = ({ elemento, puedeOperar, enviando, onOperar, recientes, onVerTodo }) => {
    const e = estadoSantamaria(elemento);
    const abierta = e.clave === 'abierta';
    const apagada = elemento.puertas.every((p) => !p.en_linea);
    const quien = e.ultimo ? quienEvento(e.ultimo) : null;
    const verbo = abierta ? 'La abrió' : 'La cerró';
    let detalle = null;
    if (e.ultimo) {
        if (e.ultimo.origen === 'cs') detalle = `${verbo} ${quien} desde Centro SURA`;
        else if (quien) detalle = `${verbo} ${quien}`;
        else detalle = abierta ? 'Se abrió sin huella' : 'Se cerró sin huella';
    }
    const puede = (p) => !!p && (p.acciones || []).includes('abrir');
    return (
        <article className={`${panel} p-5 flex flex-col lg:col-span-2 xl:col-span-1`} aria-labelledby="santamaria-titulo">
            <Escenario><Santamaria abierta={abierta} apagada={apagada} /></Escenario>
            <div className="mt-4">
                <h2 id="santamaria-titulo" className={`${display} text-lg font-bold text-slate-900`}>Santamaría</h2>
                <p className={`text-sm mt-0.5 ${apagada ? TONO.apagada : TONO[e.clave]}`}>
                    {apagada ? 'Sin conexión con el controlador'
                        : e.clave === 'desconocido' ? 'Sin registros todavía'
                            : `${abierta ? 'Abierta' : 'Cerrada'} desde ${desde(e.ultimo.fecha_hora)}`}
                </p>
                {detalle && !apagada && <p className="text-xs text-slate-400 mt-0.5">{detalle}</p>}
            </div>
            {puedeOperar && (
                <div className="mt-4">
                    <div className="grid grid-cols-2 gap-2">
                        <MantenerParaConfirmar
                            principal={!abierta} disabled={!puede(elemento.abre) || !!enviando}
                            ocupado={enviando === `${elemento.abre?.id}-abrir`}
                            onConfirmar={() => onOperar(elemento.abre, 'abrir', 'La santamaría está abriendo')}
                        >
                            Abrir
                        </MantenerParaConfirmar>
                        <MantenerParaConfirmar
                            principal={abierta} disabled={!puede(elemento.cierra) || !!enviando}
                            ocupado={enviando === `${elemento.cierra?.id}-abrir`}
                            onConfirmar={() => onOperar(elemento.cierra, 'abrir', 'La santamaría está cerrando')}
                        >
                            Cerrar
                        </MantenerParaConfirmar>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Mantén presionado para confirmar. Es lo mismo que poner la huella en el lector.</p>
                </div>
            )}
            <LoUltimo eventos={elemento.eventos} recientes={recientes} onVerTodo={onVerTodo} />
        </article>
    );
};

const TarjetaAscensor = ({ elemento, abriendo, puedeOperar, enviando, onOperar, recientes, onVerTodo }) => {
    const [mas, setMas] = useState(false);
    const p = elemento.puerta;
    const e = estadoPuerta(p);
    const abierto = abriendo || ABIERTOS.includes(e.clave);
    const piso = /P\s?(\d+)/i.exec(p.nombre)?.[1];
    const uso = elemento.eventos.find((ev) => (ev.origen === 'pase' && ev.concedido) || ev.origen === 'cs');
    const secundarias = (p.acciones || []).filter((a) => a !== 'abrir');
    return (
        <article className={`${panel} p-5 flex flex-col`} aria-labelledby={`ascensor-${p.id}`}>
            <Escenario>
                <Ascensor abierto={abierto} bloqueado={e.clave === 'bloqueada'} apagado={e.clave === 'apagada'} piso={piso} />
            </Escenario>
            <div className="mt-4">
                <h2 id={`ascensor-${p.id}`} className={`${display} text-lg font-bold text-slate-900`}>{elemento.nombre}</h2>
                <p className={`text-sm mt-0.5 ${abriendo && e.clave === 'cerrada' ? 'text-cyan-700' : TONO[e.clave]}`}>
                    {abriendo && e.clave === 'cerrada' ? 'Abierto un momento' : e.texto}
                </p>
                {uso && (
                    <p className="text-xs text-slate-400 mt-0.5">
                        Último uso: {quienEvento(uso) || 'sin nombre'}{uso.origen === 'cs' ? ' desde Centro SURA' : ''}, {cuandoFue(uso.fecha_hora)}
                    </p>
                )}
            </div>
            {puedeOperar && (
                <div className="mt-4">
                    <MantenerParaConfirmar
                        className="w-full" disabled={!(p.acciones || []).includes('abrir') || !!enviando}
                        ocupado={enviando === `${p.id}-abrir`}
                        onConfirmar={() => onOperar(p, 'abrir', `${elemento.nombre} abierto un momento`)}
                    >
                        Abrir un momento
                    </MantenerParaConfirmar>
                    <p className="text-xs text-slate-400 mt-2">Mantén presionado para confirmar.</p>
                    {secundarias.length > 0 && (
                        <>
                            <button
                                type="button" onClick={() => setMas((v) => !v)} aria-expanded={mas}
                                className="mt-3 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                            >
                                Más opciones <ChevronDown size={15} className={`transition-transform motion-reduce:transition-none ${mas ? 'rotate-180' : ''}`} />
                            </button>
                            {mas && (
                                <div className="mt-2 space-y-3 puertas-aparece">
                                    {secundarias.map((a) => (
                                        <div key={a}>
                                            <MantenerParaConfirmar
                                                className="w-full" principal={false} peligro={a === 'bloquear'}
                                                disabled={!!enviando} ocupado={enviando === `${p.id}-${a}`}
                                                onConfirmar={() => onOperar(p, a, `${elemento.nombre} ${ACCIONES[a].hecho}`)}
                                            >
                                                {ACCIONES[a].nombre}
                                            </MantenerParaConfirmar>
                                            <p className="text-xs text-slate-400 mt-1 px-1">{ACCIONES[a].ayuda}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            <LoUltimo eventos={elemento.eventos} recientes={recientes} onVerTodo={onVerTodo} />
        </article>
    );
};

const RECIENTE_MS = 3000;

/**
 * La pantalla principal: la santamaría y los ascensores, cada uno dibujado como es, con
 * cómo está, lo último que pasó y, si tu huella la abre a esta hora, el botón para abrir o
 * cerrar. Se actualiza sola cuando alguien pasa.
 */
const PuertasVista = ({ vivo, irA }) => {
    const { estado, error, enVivo, recargar, suscribir } = vivo;
    const [abriendo, setAbriendo] = useState({});   // puerta id -> true mientras se dibuja abierta
    const [recientes, setRecientes] = useState(() => new Set());
    const [enviando, setEnviando] = useState(null);

    const elementos = useMemo(() => armarElementos(estado?.puertas || [], estado?.ultimos), [estado]);

    const abrirUnMomento = useCallback((id) => {
        setAbriendo((a) => ({ ...a, [id]: true }));
        setTimeout(() => setAbriendo((a) => {
            const n = { ...a };
            delete n[id];
            return n;
        }), MOMENTO_MS);
    }, []);

    // Cada pase anima su ascensor y se ilumina un momento en la lista
    useEffect(() => suscribir((ev) => {
        const abre = (ev.origen === 'pase' && ev.concedido) || ev.tipo === 'desbloqueada' || (ev.origen === 'cs' && ev.accion === 'abrir');
        if (abre && !esSantamaria(ev)) abrirUnMomento(ev.puerta_id);
        setRecientes((prev) => new Set(prev).add(ev.id));
        setTimeout(() => setRecientes((prev) => {
            const n = new Set(prev);
            n.delete(ev.id);
            return n;
        }), RECIENTE_MS);
    }), [suscribir, abrirUnMomento]);

    const operar = async (puerta, accion, aviso) => {
        setEnviando(`${puerta.id}-${accion}`);
        try {
            await axiosInstance.post(`/api/puertas/${puerta.id}/operar`, { accion });
            toast.success(aviso);
            if (accion === 'abrir' && !esSantamaria(puerta)) abrirUnMomento(puerta.id);
            setTimeout(recargar, 1500);
        } catch (err) {
            toast.error(err.response?.data?.error || 'GeoVision no aceptó la operación');
        } finally {
            setEnviando(null);
        }
    };

    if (error && !estado) {
        return (
            <div className={`${panel} py-12 text-center`}>
                <p className="font-medium text-slate-700">No hay conexión con el control de puertas</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{error}. Revisa que el servidor de GeoVision (192.168.141.30) esté encendido.</p>
                <button type="button" onClick={recargar} className={`${botonClaro} mt-4`}><RefreshCw size={14} /> Reintentar</button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Encabezado titulo="Puertas" acciones={estado && <EnVivo texto={enVivo ? 'En vivo' : 'Se actualiza cada 20 segundos'} />}>
                {estado ? resumen(elementos) : 'Consultando el control de puertas.'}
            </Encabezado>
            {!estado ? <Cargando /> : (
                <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-[1.3fr_1fr_1fr] items-start">
                    {elementos.map((el) => (el.tipo === 'santamaria' ? (
                        <TarjetaSantamaria
                            key={el.clave} elemento={el} puedeOperar={el.puertas.some((p) => p.acciones.length > 0)} enviando={enviando}
                            onOperar={operar} recientes={recientes} onVerTodo={() => irA('registros', { elemento: 'santamaria' })}
                        />
                    ) : (
                        <TarjetaAscensor
                            key={el.clave} elemento={el} abriendo={!!abriendo[el.puerta.id]} puedeOperar={el.puertas.some((p) => p.acciones.length > 0)}
                            enviando={enviando} onOperar={operar} recientes={recientes}
                            onVerTodo={() => irA('registros', { elemento: String(el.puerta.id) })}
                        />
                    )))}
                </div>
            )}
        </div>
    );
};

export default PuertasVista;
